import React, { FC, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingResource, pricingResourceItem } from "../../../api/endpoints";
import { TierRow, usePricing } from "./PricingContext";
import { SnapshotBanner } from "./SnapshotBanner";

type MinField = "min_projects" | "min_distinct_projects";

interface TierEditorProps {
    title: string;
    resource: "project-count-tiers" | "matching-product-tiers";
    minField: MinField;
    minLabel: string;
    rows: TierRow[];
}

export const TierEditor: FC<TierEditorProps> = ({ title, resource, minField, minLabel, rows }) => {
    const { refetch, canEdit } = usePricing();
    const [busyId, setBusyId] = useState<number | null>(null);
    const [draftMin, setDraftMin] = useState("");
    const [draftPct, setDraftPct] = useState("");

    const sorted = [...rows].sort(
        (a: any, b: any) => (Number(a[minField]) || 0) - (Number(b[minField]) || 0)
    );

    const handlePatch = async (id: number, patch: Partial<TierRow>) => {
        setBusyId(id);
        try {
            await axiosInstant.patch(pricingResourceItem(resource, id), patch);
            toast.success("Saved");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id: number) => {
        setBusyId(id);
        try {
            await axiosInstant.delete(pricingResourceItem(resource, id));
            toast.success("Deleted");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to delete");
        } finally {
            setBusyId(null);
        }
    };

    const handleCreate = async () => {
        const minNum = Number(draftMin);
        const pctNum = Number(draftPct);
        if (!Number.isFinite(minNum) || minNum < 0 || !Number.isInteger(minNum)) {
            toast.error(`${minLabel} must be a non-negative integer`);
            return;
        }
        if (!Number.isFinite(pctNum) || pctNum < 0) {
            toast.error("Discount percentage must be a non-negative number");
            return;
        }
        try {
            await axiosInstant.post(pricingResource(resource), {
                [minField]: minNum,
                discount_pct: pctNum
            });
            toast.success("Tier added");
            setDraftMin("");
            setDraftPct("");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to add tier");
        }
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                {title}
            </Typography>
            <SnapshotBanner />
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>{minLabel}</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Discount %</strong>
                            </TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((row: any) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        defaultValue={row[minField]}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value === "") {
                                                // Cleared, not zeroed — Number("") is 0 and a
                                                // 0-threshold tier discounts every order.
                                                e.target.value = String(row[minField]);
                                                return;
                                            }
                                            const v = Number(e.target.value);
                                            if (Number.isFinite(v) && v !== Number(row[minField])) {
                                                handlePatch(row.id, { [minField]: v } as any);
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        defaultValue={row.discount_pct}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value === "") {
                                                // Cleared, not zeroed — restore instead of
                                                // silently saving a 0% discount.
                                                e.target.value = String(row.discount_pct);
                                                return;
                                            }
                                            const v = Number(e.target.value);
                                            if (Number.isFinite(v) && v !== Number(row.discount_pct)) {
                                                handlePatch(row.id, { discount_pct: v });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(row.id)}
                                            disabled={busyId === row.id}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {canEdit && (
                        <TableRow>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder={minLabel}
                                    value={draftMin}
                                    onChange={(e) => setDraftMin(e.target.value)}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder="0"
                                    value={draftPct}
                                    onChange={(e) => setDraftPct(e.target.value)}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreate}
                                    disabled={!draftMin || !draftPct}
                                >
                                    Add
                                </Button>
                            </TableCell>
                        </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
